import { RollingQuantile } from "./filters.ts";
import type { SignalFrame, SignalKind } from "./types.ts";

/**
 * Converts either raw signal into one normalised "closure" value: 0 = the
 * subject's own open-eye baseline, 1 = fully shut.
 *
 * This is what makes a single set of thresholds work across both detection
 * routes and across people. Spec §2 is emphatic that a fixed EAR threshold does
 * not survive individual variation in eye geometry — normalising against a
 * *personal* baseline is the fix, and doing it here means the state machine
 * downstream never has to know which signal it is looking at.
 *
 * The baseline is adaptive rather than purely calibration-derived. Calibration
 * (spec §3) still refines it, but a rolling estimate means drifting light or a
 * pair of glasses going on mid-session self-corrects instead of requiring the
 * user to notice the detector has gone wrong and re-run a wizard.
 */
/**
 * Openness at full closure, as a fraction of the subject's open baseline.
 *
 * This is a physical prior, and it deliberately does NOT adapt. An earlier
 * version estimated it from a rolling low quantile of the signal, which was
 * wrong in a way worth recording: the estimate tracks the deepest closure the
 * subject has actually made, so it rescales whatever they do to "fully closed".
 * Someone who only ever half-blinks would have every half-blink reported as
 * complete — which destroys the one mechanic (spec §8) that depends on telling
 * the difference, and quietly makes the partial-blink metric meaningless.
 *
 * Only the OPEN baseline adapts. That is the one that genuinely drifts, with
 * light and with glasses going on and off.
 */
const CLOSED_FRACTION: Record<SignalKind, number> = {
  // A shut eye scores ~1 on eyeBlink, so openness (1 - blink) goes to ~0.
  blendshape: 0.05,
  // A shut eye still has measurable EAR — lashes and lid thickness. Roughly a
  // fifth of the open value across the faces in the literature. Calibration
  // (spec §3, step 3) replaces this prior with the subject's real floor.
  ear: 0.2,
};

export class ClosureSignal {
  readonly #window: RollingQuantile;
  readonly #warmupSamples: number;
  readonly #closedFraction: number;
  #closedAbsolute: number | null = null;

  constructor(
    signal: SignalKind,
    closedFraction?: number,
    windowSamples = 300,
    warmupSamples = 20,
  ) {
    this.#window = new RollingQuantile(windowSamples);
    this.#warmupSamples = warmupSamples;
    this.#closedFraction = closedFraction ?? CLOSED_FRACTION[signal];
  }

  /** Supplies the measured closed floor from calibration, superseding the prior. */
  setClosedReference(openness: number): void {
    this.#closedAbsolute = Number.isFinite(openness) ? openness : null;
  }

  /** True once enough samples exist for the baseline to mean anything. */
  get ready(): boolean {
    return this.#window.size >= this.#warmupSamples;
  }

  get openBaseline(): number {
    return this.#window.quantile(0.9);
  }

  /** Openness expected at full closure — from calibration if it has run. */
  get closedReference(): number {
    return this.#closedAbsolute ?? this.openBaseline * this.#closedFraction;
  }

  /**
   * @param openness higher = more open. EAR directly; for blendshapes, 1 - blink.
   * @returns normalised closure, clamped to 0..1.
   */
  push(openness: number): number {
    if (!Number.isFinite(openness)) return Number.NaN;
    this.#window.push(openness);

    const open = this.openBaseline;
    if (!Number.isFinite(open)) return Number.NaN;

    const range = Math.max(open - this.closedReference, 1e-4);
    return clamp01((open - openness) / range);
  }

  reset(): void {
    this.#window.reset();
    this.#closedAbsolute = null;
  }
}

export const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

/** Extracts the openness value for one eye under the chosen detection route. */
export function opennessOf(frame: SignalFrame, kind: SignalKind, eye: "left" | "right"): number {
  return kind === "ear" ? frame.ear[eye] : 1 - frame.blink[eye];
}
